import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, MapPin, Plus } from "lucide-react";

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ActivityFeed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/activity", { params: { limit: 6 } })
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) return null;

  return (
    <section className="py-24 bg-[#F9F6F0] border-t border-border" data-testid="activity-feed">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">In motion</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">What's brewing this week.</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="border border-border bg-card rounded-sm h-32 animate-pulse" />
            ))
          ) : events.map((e, i) => (
            <Link
              key={`${e.type}-${e.cafe_id}-${i}`}
              to={`/cafe/${e.cafe_id}`}
              data-testid={`activity-item-${i}`}
              className="border border-border bg-card rounded-sm p-6 grid grid-cols-[88px_1fr] gap-5 hover:-translate-y-0.5 transition-transform"
            >
              <div className="aspect-square bg-[#E8DFD3] rounded-sm overflow-hidden">
                {e.cafe_image && (
                  <img src={e.cafe_image} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#C05A46]">
                  {e.type === "review" ? (
                    <><Star size={11} strokeWidth={1.5} className="fill-[#C05A46]" /> New review</>
                  ) : (
                    <><Plus size={12} strokeWidth={1.5} /> New listing</>
                  )}
                  <span className="text-[#786B63]">· {timeAgo(e.created_at)}</span>
                </div>
                <p className="font-serif text-2xl mt-1 leading-tight truncate">{e.cafe_name}</p>
                <p className="text-xs text-[#786B63] mt-1 inline-flex items-center gap-1.5">
                  <MapPin size={11} strokeWidth={1.5} /> {e.cafe_city}
                </p>
                {e.type === "review" ? (
                  <p className="text-sm text-[#786B63] mt-2 line-clamp-2 leading-relaxed">
                    <span className="text-[#2C1E16]">{e.user_name}</span> · {e.rating}/5 — "{e.snippet}"
                  </p>
                ) : (
                  <p className="text-sm text-[#786B63] mt-2 line-clamp-2 leading-relaxed">
                    Submitted by {e.submitted_by_name || "the community"}.
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
